import { setApiUrl, setPlatformHeader } from "@ac-expo/api";
import {
  Toaster,
  TooltipProvider,
  useIsMobile,
  VersionBadge,
} from "@ac-expo/ui-components";
import { QueryClientProvider } from "@tanstack/react-query";
import "galadrim-feedback/dist/styles.css";
import { enableStaticRendering } from "mobx-react-lite";
import { NextPage } from "next";
import { AppProps } from "next/app";
import dynamic from "next/dynamic";
import { useRouter } from "next/router";
import { NuqsAdapter } from "nuqs/adapters/next/pages";
import { ReactElement, ReactNode, useEffect, useState } from "react";
import CartPanel from "../components/Cart/CartPanel";
import DownloadAppsBanner from "../components/DownloadAppsBanner/DownloadAppsBanner";
import Footer from "../components/Element/FooterDesktop";
import FooterMobile from "../components/Element/FooterMobile";
import { NavMenu } from "../components/Navigation/NavMenu";
import { UserProvider } from "../context/auth/user.context";
import { CartProvider } from "../context/cart.context";
import { GoogleApiProvider } from "../context/googleApi.context";
import { UserGeolocationProvider } from "../context/userGeolocalisation.context";
import { MessageProvider } from "../context/websocket/message.context";
import { NotificationsProvider } from "../context/websocket/notifications.context";
import { WebSocketProvider } from "../context/websocket/websocket.context";
import environment from "../environments/environment";
import { authClient } from "../services/auth-client";
import { createQueryClient } from "../shared/queryClient";
import "@ui/components/ui/planning/planning.css";
import "./styles.css";
import { ChatbotAdapter } from "../context/chatbot-adapter";
import useRedirectCashRegisterHolder from "../hooks/useRedirectCashRegisterHolder";
import useRequireCompleteProfile from "../hooks/useRequireCompleteProfile";

enableStaticRendering(typeof window === "undefined");

const V2_RESET_KEY = "v2-data-reset";
if (typeof window !== "undefined" && !localStorage.getItem(V2_RESET_KEY)) {
  localStorage.clear();
  sessionStorage.clear();
  authClient.signOut().catch(() => {});
  localStorage.setItem(V2_RESET_KEY, "done");
}

setApiUrl(environment.API_URL);
setPlatformHeader("webApp");

const FeedbackRoot = dynamic(() => import("galadrim-feedback"), { ssr: false });

function ProfileCompleteGuard({ children }: { children: ReactNode }) {
  useRequireCompleteProfile();
  return <>{children}</>;
}

function CashRegisterHolderGuard({ children }: { children: ReactNode }) {
  useRedirectCashRegisterHolder();

  return <>{children}</>;
}

function ClientOnlyFeedback() {
  const [isClient, setIsClient] = useState(false);
  const router = useRouter();

  useEffect(() => {
    setIsClient(true);
  }, []);

  const isPreprod = environment.API_URL?.includes("preprod");

  if (!isClient || !isPreprod) {
    return null;
  }

  return (
    <FeedbackRoot
      rootDir="/Users/antoineliu/ac-expo"
      projectId="8086c22e-4665-4992-a74c-4c3888092b8c"
      navigate={(path: string) => {
        router.push(path);
      }}
      position="bottom-left"
    />
  );
}

function StagingVersionBadge() {
  const isVisible =
    process.env.NODE_ENV === "development" ||
    environment.API_URL?.includes("staging");
  const version = process.env.NEXT_PUBLIC_APP_VERSION;

  if (!isVisible || !version) {
    return null;
  }

  return <VersionBadge version={version} />;
}

/*
 * Manage different types of layout
 * cf. https://nextjs.org/docs/14/pages/building-your-application/routing/pages-and-layouts#per-page-layouts
 */
export type NextPageWithLayout<P = object, IP = P> = NextPage<P, IP> & {
  getLayout?: (page: ReactElement) => ReactNode;
};

type AppPropsWithLayout = AppProps & {
  Component: NextPageWithLayout;
};

function CustomApp({ Component, pageProps }: AppPropsWithLayout) {
  const [queryClient] = useState(createQueryClient);
  const isMobile = useIsMobile();
  const getLayout =
    Component.getLayout ??
    ((page) => (
      <>
        <UserGeolocationProvider>
          <div className="flex min-h-screen flex-col">
            <DownloadAppsBanner />
            <NavMenu />
            {page}
            {isMobile ? <FooterMobile /> : <Footer />}
          </div>
          <CartPanel />
          <ChatbotAdapter />
          <ClientOnlyFeedback />
          <StagingVersionBadge />
        </UserGeolocationProvider>
      </>
    ));

  return (
    <>
      <QueryClientProvider client={queryClient}>
        <Toaster />
        <TooltipProvider>
          <GoogleApiProvider>
            <NuqsAdapter>
              <UserProvider>
                <ProfileCompleteGuard>
                  <CashRegisterHolderGuard>
                    <WebSocketProvider>
                      <NotificationsProvider>
                        <CartProvider>
                          <MessageProvider>
                            {getLayout(<Component {...pageProps} />)}
                          </MessageProvider>
                        </CartProvider>
                      </NotificationsProvider>
                    </WebSocketProvider>
                  </CashRegisterHolderGuard>
                </ProfileCompleteGuard>
              </UserProvider>
            </NuqsAdapter>
          </GoogleApiProvider>
        </TooltipProvider>
      </QueryClientProvider>
    </>
  );
}

export default CustomApp;
